import React from "react";
import { billingRateLabel, formatBillingMoney } from "../../utils/billingCalculation.js";
import { kmLabel, tonsLabel } from "../../utils/billingCalculationDays.js";
import { distanceLabel } from "../../utils/transportStatement.js";

const PHOTOS = "/brand/releve/";
const GEOTAB_LOGO = "/brand/geotab-logo.svg";
// Chaque feuille est une page Lettre : les voyages qui ne tiennent pas continuent sur la suivante.
const TRIPS_FIRST_PAGE = 7;
const TRIPS_PER_PAGE = 10;
const PROOFS_PER_PAGE = 4;

const joined = (...parts) => parts.filter(Boolean).join(" · ");
const plural = (count, word, many = `${word}s`) => `${count} ${count > 1 ? many : word}`;
const sumKnown = (values) => values.some((value) => value == null) ? null : values.reduce((total, value) => total + value, 0);
const duration = (seconds) => {
  if (seconds == null) return "—";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const rest = minutes % 60;
  return `${Math.floor(minutes / 60)} h${rest ? ` ${String(rest).padStart(2, "0")}` : ""}`;
};
// Temps d'un arrêt sans heure d'arrivée : « ≈ » partout, « estimé » sur sa ligne.
const about = (estimated, seconds) => `${estimated ? "≈ " : ""}${duration(seconds)}`;
const Estimated = ({ on }) => on ? <span className="ts-estimated">estimé</span> : null;
const hoursLabel = (hours) => hours == null ? "—" : duration(hours * 3600);
const percent =(share) => `${Math.round(share * 100)} %`;
const clock = (value, timeZone) => value ? new Intl.DateTimeFormat("fr-CA", { hour: "numeric", minute: "2-digit", timeZone }).format(new Date(value)) : "";
const minuteClock = (minutes) => {
  if (minutes == null) return "";
  const total = Math.round(minutes);
  return `${Math.floor(total / 60)} h ${String(total % 60).padStart(2, "0")}`;
};
const dateText = (date, options) => date ? new Intl.DateTimeFormat("fr-CA", { ...options, timeZone: "UTC" }).format(new Date(`${date}T12:00:00Z`)) : "";
const capital = (text) => text.charAt(0).toUpperCase() + text.slice(1);
const shortDay = (date) => capital(dateText(date, { weekday: "short", day: "numeric", month: "short" }));
const longDay = (date) => capital(dateText(date, { weekday: "long", day: "numeric", month: "long" }));
// Le relevé nomme le camion, jamais le chauffeur.
const truckLabel = (voyage) => voyage.truck ? `Camion ${voyage.truck}` : "Camion non indiqué";
// Chargé PM : sa livraison fait un autre voyage, le soir même ou un autre jour.
const pmDelivery = (pm) => `livré le ${pm.period === "evening" ? "soir du " : ""}${dateText(pm.date, { day: "numeric", month: "short" })}`;
// Montant qui sera facturé : taxes comprises quand le relevé porte la TPS et la TVQ (Paramètres), comme la facture.
const billedAmount = (amounts) => amounts.taxes && amounts.total != null ? amounts.total : amounts.beforeTaxes;
const percentLabel = (value) => `${new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 3 }).format(value)} %`;
const taxLines = (amounts) => amounts.taxes ? [[`TPS · ${percentLabel(amounts.gstPercent)}`, amounts.gst], [`TVQ · ${percentLabel(amounts.qstPercent)}`, amounts.qst]] : [];
const chunk = (values, first, size, weight = () => 1) => {
  const pages = [[]];
  let room = first;
  for (const value of values) {
    if (pages.at(-1).length && weight(value) > room + 1e-9) { pages.push([]); room = size; }
    pages.at(-1).push(value);
    room -= weight(value);
  }
  return pages;
};
// Premier numéro de commande complet ; les suivants gardent seulement la fin qui les distingue du premier : « SOLP-00632720, …722, …5599 ».
const orderRefs = (references) => references.map((reference, index) => {
  let same = 0;
  while (index && same < reference.length && reference[same] === references[0][same]) same += 1;
  same = Math.min(same, reference.length - 3);
  return same >= 4 ? `…${reference.slice(same)}` : reference;
});
// Environ 34 caractères de numéros par ligne ; chaque ligne de plus allonge le voyage d'un cinquième.
const tripWeight = (voyage) => 1 + Math.max(0, Math.ceil(orderRefs(voyage.orders).join(", ").length / 34) - 1) * 0.2;

function periodText(start, end) {
  if (!start) return "";
  const full = { day: "numeric", month: "long", year: "numeric" };
  if (!end || start === end) return `le ${dateText(start, full)}`;
  const sameYear = start.slice(0, 4) === end.slice(0, 4);
  const from = dateText(start, start.slice(0, 7) === end.slice(0, 7) ? { day: "numeric" } : sameYear ? { day: "numeric", month: "long" } : full);
  return `du ${from} au ${dateText(end, full)}`;
}

/** Pictogrammes au trait du cartable : meunerie, route, ferme, camion. */
function Icons() {
  const line = { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinejoin: "round", strokeLinecap: "round" };
  return <svg width="0" height="0" className="ts-defs" aria-hidden="true" focusable="false">
    <defs>
      <symbol id="ts-m-mill" viewBox="0 0 48 48"><g {...line}><path d="M5 42h38" /><path d="M8 42V20a4 4 0 0 1 8 0v22M16 42V18a4 4 0 0 1 8 0v24" /><path d="M26 42V11h8v31M25 11l5-5 5 5" /><path d="M34 17l8 8v17" /><path d="M8 28h16M8 35h16" /></g></symbol>
      <symbol id="ts-m-road" viewBox="0 0 48 48"><g {...line}><path d="M19 6L8 42M29 6l11 36" /><path d="M24 9v5M24 19v6M24 30v8" /><circle cx="41" cy="12" r="3.5" /><path d="M41 15.5v6" /></g></symbol>
      <symbol id="ts-m-farm" viewBox="0 0 48 48"><g {...line}><path d="M4 42h40" /><path d="M6 42V24l10-8 10 8v18" /><path d="M12 42V31h8v11M12 31l8 11M20 31l-8 11" /><path d="M31 42V19l4.5-4.5L40 19v23M31 26h9M31 33h9" /></g></symbol>
      <symbol id="ts-m-truck" viewBox="0 0 48 48"><g {...line}><path d="M4 33V15h26v18M30 20h8l6 7v6H30" /><circle cx="12" cy="36" r="3" /><circle cx="23" cy="36" r="3" /><circle cx="38" cy="36" r="3" /><path d="M11 24l3.5 3.5L21 21" /></g></symbol>
      <symbol id="ts-m-start" viewBox="0 0 48 48"><g {...line}><path d="M4 33V15h26v18M30 20h8l6 7v6H30" /><circle cx="12" cy="36" r="3" /><circle cx="23" cy="36" r="3" /><circle cx="38" cy="36" r="3" /></g></symbol>
    </defs>
  </svg>;
}

const Icon = ({ name, className = "" }) => <svg className={className} aria-hidden="true" focusable="false"><use href={`#ts-m-${name}`} /></svg>;
const Photo = ({ name, className = "ts-photo" }) => <div className={className} style={{ backgroundImage: `url('${PHOTOS}${name}')` }} aria-hidden="true" />;

/** Logo de l'émetteur (Paramètres) ; sans logo, son nom. « light » : sur une photo. */
function Brand({ model, light = false }) {
  const tone = light ? " is-light" : "";
  return model.issuerLogo ? <img className={`ts-logo${tone}`} src={model.issuerLogo} alt={model.issuer.name || "Logo du transporteur"} />
    : <strong className={`ts-wordmark${tone}`}>{model.issuer.name || "Transporteur"}</strong>;
}

function Tabs({ tabs, active }) {
  return <div className="ts-tabs" aria-hidden="true">{tabs.map((tab) => <span key={tab} className={tab === active ? "is-on" : ""}>{tab}</span>)}</div>;
}

const Folio = ({ page }) => <div className="ts-folio"><span>Relevé de transport</span><b>{page}</b></div>;

function SectionHead({ number, title, note }) {
  return <div className="ts-sec-head"><div className="ts-no">{number}</div><div><h2>{title}</h2><p>{note}</p></div></div>;
}

function Band({ photo, title, note }) {
  return <div className="ts-band"><Photo name={photo} className="ts-band-photo" /><div className="ts-band-text"><b>{title}</b><span>{note}</span></div></div>;
}

// Le service rendu en gros chiffres : tonnes, commandes, voyages et journées (couverture et L'essentiel).
function ServiceFacts({ counts }) {
  const tons = tonsLabel(counts.tons);
  const facts = [[tons ? <>{tons.slice(0, -2)}<small> t</small></> : "—", "Livrées"], [counts.orders, "Commandes"], [counts.voyages, "Voyages"], [counts.days, "Journées"]];
  return <dl className="ts-kpi">{facts.map(([value, label]) => <div key={label}><dt>{label}</dt><dd className="ts-num">{value}</dd></div>)}</dl>;
}

/** Couverture : le service rendu, sans le montant, présenté en conclusion de L'essentiel. */
function Cover({ model }) {
  return <section className="ts-sheet ts-cover-page" id="ts-couverture" aria-label="Couverture du relevé">
    <Photo name="meunerie.jpg" /><div className="ts-shade" />
    <div className="ts-cover-top"><Brand model={model} light /><div className="ts-ref">Relevé de transport<br />{model.number || <span className="ts-preview">Aperçu · relevé non envoyé</span>}</div></div>
    <div className="ts-cover-body">
      <div className="ts-kicker">Période {periodText(model.periodStart, model.periodEnd)}</div>
      <h1>Relevé de<br />transport</h1>
      <div className="ts-rule" />
      <div className="ts-for">Préparé à l’intention de<strong>{model.clientName}</strong></div>
      {model.clientLogo ? <img className="ts-client-logo" src={model.clientLogo} alt={`Logo de ${model.clientName}`} /> : null}
    </div>
    <div className="ts-cover-service">
      <div className="ts-label">Service rendu</div>
      <div className="ts-cover-big ts-num">{hoursLabel(model.hours)}<small>de transport pour vos fermes</small></div>
      <ServiceFacts counts={model.counts} />
    </div>
    <div className="ts-cover-foot"><span>Préparé le {new Intl.DateTimeFormat("fr-CA", { dateStyle: "long" }).format(model.preparedOn)}</span><span>Le montant est détaillé en page 3</span></div>
  </section>;
}

function Letter({ model, sections }) {
  const { counts, amounts } = model;
  const tons = counts.tons == null ? "" : `${tonsLabel(counts.tons).replace(" t", "")} tonnes acheminées`;
  return <section className="ts-sheet ts-letter-page" id="ts-mot" aria-label="Mot d’accompagnement et sommaire">
    <div className="ts-letter">
      <Brand model={model} />
      <div className="ts-label">Mot d’accompagnement</div>
      <h2>Le détail de votre transport, en toute transparence.</h2>
      <p>Madame, Monsieur,</p>
      <p>Nous avons le plaisir de vous présenter le relevé des services de transport réalisés pour votre entreprise {periodText(model.periodStart, model.periodEnd)} : {[plural(counts.voyages, "voyage"), plural(counts.farms, "ferme livrée", "fermes livrées"), tons].filter(Boolean).join(", ").replace(/, ([^,]*)$/, " et $1")}.</p>
      <p>Les informations de nos camionneurs sont recueillies avec Geotab, utilisé dans chacun de nos camions, puis revues par notre équipe de répartition. Vous pouvez ainsi suivre, voyage par voyage, le temps passé en meunerie, sur la route et chez vos producteurs.</p>
      <p>La facture correspondante vous parviendra dans les prochains jours. Elle reprendra le montant de ce relevé{amounts.taxes ? ", taxes comprises" : ""}.</p>
      <p>Nous vous remercions de votre confiance et demeurons à votre disposition pour toute question.</p>
      <div className="ts-sign"><strong>L’équipe de la facturation</strong>{joined(model.issuer.email, model.issuer.phone)}</div>
      <div className="ts-quote">« Les données Geotab de nos camions et le suivi de notre équipe vous donnent une vue détaillée de vos transports. »<small>Notre engagement envers votre entreprise</small></div>
      <div className="ts-meta">{["Relevé de transport", model.number, "Document confidentiel"].filter(Boolean).join(" · ")}</div>
    </div>
    <aside className="ts-toc">
      <div className="ts-toc-in">
        <div className="ts-label">Sommaire</div>
        <h3>Ce que contient ce relevé</h3>
        <ol>{sections.map((section) => <li key={section.number}><b>{section.number}</b><span>{section.title}<small>{section.note}</small></span><i>{section.page}</i></li>)}</ol>
      </div>
      <Photo name="ferme.jpg" />
    </aside>
  </section>;
}

// Ce que la route compte en plus des trajets, « départ et retour au garage » (un dompeur n'a pas de retour facturé).
function garageWords({ departure, back }, article = "") {
  const words = [departure && `${article}départ`, back && `${article}retour`].filter(Boolean).join(" et ");
  return words && `${words} ${back ? "au" : "du"} garage`;
}

function Essentials({ model, page, tabs }) {
  const { amounts, counts, time, currency } = model;
  const rate = model.rate ? billingRateLabel(model.rate.rate, "heure", currency) : "";
  const total = formatBillingMoney(billedAmount(amounts), currency);
  const figure = total.endsWith(" $") ? total.slice(0, -2) : null;
  const garage = garageWords(time.route);
  const steps = [
    time.loading.count ? { icon: "mill", title: "Chargement", value: duration(time.loading.seconds), estimated: time.loading.estimated, note: `En meunerie · ${plural(time.loading.count, "chargement")}` } : null,
    time.route.count || time.route.seconds ? { icon: "road", title: "Route", value: duration(time.route.seconds), estimated: time.route.estimated,
      note: joined(kmLabel(time.route.km), garage ? `${garage} compris` : "vers la meunerie et vos fermes") } : null,
    time.delivery.count ? { icon: "farm", title: "Livraison", value: duration(time.delivery.seconds), estimated: time.delivery.estimated, note: "Sur place chez vos producteurs" } : null,
    time.other ? { icon: "truck", title: "Autre temps", value: duration(time.other), note: "Sans détail du trajet" } : null,
  ].filter(Boolean);
  const parts = [["loading", "Chargement", time.loading.seconds], ["road", "Route", time.route.seconds], ["delivery", "Livraison", time.delivery.seconds],
    ["other", "Autre", time.other]].filter(([, , seconds]) => seconds > 0);
  const spent = parts.reduce((sum, [, , seconds]) => sum + seconds, 0);
  return <section className="ts-sheet ts-essentials" id="ts-essentiel" aria-label="01 L’essentiel">
    <div className="ts-opener"><Photo name="gavel.jpg" className="ts-opener-photo" /><Brand model={model} light />
      <div className="ts-opener-text"><div className="ts-no">01</div><h2>L’essentiel</h2><p>Ce que comprend votre relevé {periodText(model.periodStart, model.periodEnd)}</p></div></div>
    <Tabs tabs={tabs} active="01" />
    <div className="ts-inner is-tight">
      <div className="ts-service">
        <div><div className="ts-label">Service rendu</div>
          <div className="ts-big ts-num">{hoursLabel(model.hours)}</div>
          <div className="ts-cap">de transport pour vos fermes</div></div>
        {/* Le service rendu passe avant le montant. */}
        <ServiceFacts counts={counts} />
      </div>
      <div className="ts-steps" aria-label="Calcul des heures facturées">
        {steps.map((step, index) => <div key={step.title} className="ts-step">
          {index ? <span className="ts-op" aria-hidden="true">+</span> : null}
          <div className="ts-ic"><Icon name={step.icon} /></div>
          <div className="ts-t">{step.title}</div><div className="ts-v ts-num">{step.value}</div>
          <div className="ts-n">{step.note}{step.estimated ? <><br />dont ≈ {duration(step.estimated)} estimées</> : null}</div>
        </div>)}
        <div className="ts-step is-result"><span className="ts-op" aria-hidden="true">=</span>
          <div className="ts-t">Heures facturées</div><div className="ts-v ts-num">{hoursLabel(model.hours)}</div>
          <div className="ts-n">{rate ? <>× {rate}<br />= </> : null}{formatBillingMoney(amounts.transport, currency)}</div></div>
      </div>
      {spent ? <div className="ts-share">
        <h4>Où vont vos {hoursLabel(model.hours)}</h4>
        <div className="ts-stack">{parts.map(([kind, label, seconds]) => <div key={kind} className={`is-${kind}`} style={{ width: `${seconds / spent * 100}%` }}>{label}</div>)}</div>
        <div className="ts-stack-legend">{parts.map(([kind, , seconds]) => <div key={kind} style={{ width: `${seconds / spent * 100}%` }}>
          <b className="ts-num">{duration(seconds)}</b> · {percent(seconds / spent)}</div>)}</div>
      </div> : null}
      {/* Le montant vient en conclusion, après le service rendu. */}
      <div className="ts-amount">
        <div><div className="ts-label">En conclusion · montant qui sera facturé</div>
          <div className="ts-amount-big ts-num">{figure ? <>{figure} <small>$</small></> : total}</div>
          <div className="ts-cap">{amounts.taxes ? "Taxes comprises" : "Avant taxes"} · la facture suivra dans les prochains jours</div></div>
        <dl>
          <div><dt>{joined("Transport", rate ? `${hoursLabel(model.hours)} × ${rate}` : hoursLabel(model.hours))}</dt><dd>{formatBillingMoney(amounts.transport, currency)}</dd></div>
          {amounts.extras ? <div><dt>Autres frais</dt><dd>{formatBillingMoney(amounts.extras, currency)}</dd></div> : null}
          {amounts.surchargePercent ? <div><dt>Surtaxe carburant · {new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 2 }).format(amounts.surchargePercent)}{" "}%</dt>
            <dd>{formatBillingMoney(amounts.surcharge, currency)}</dd></div> : null}
          {/* TPS et TVQ sur une ligne : la page garde sa hauteur. */}
          {amounts.taxes ? <div><dt>{taxLines(amounts).map(([label]) => label).join(" · ")}</dt>
            <dd>{taxLines(amounts).map(([, value]) => formatBillingMoney(value, currency)).join(" · ")}</dd></div> : null}
          <div className="is-total"><dt>{amounts.taxes ? "Total taxes comprises" : "Total avant taxes"}</dt><dd>{total}</dd></div>
        </dl>
      </div>
      <div className="ts-pledges">
        <div><Icon name="road" className="ts-pledge-ic" /><b>Un suivi avec Geotab</b><p>Les données de nos camions complètent le détail des services de transport présentés.</p></div>
        <div><Icon name="truck" className="ts-pledge-ic" /><b>Des journées vérifiées</b><p>Notre équipe de répartition revoit chaque journée avant de vous la présenter.</p></div>
        <div><Icon name="farm" className="ts-pledge-ic" /><b>Aucune surprise</b><p>La facture reprendra exactement ce montant{amounts.taxes ? ", taxes comprises" : ""}.</p></div>
      </div>
    </div>
    <Folio page={page} />
  </section>;
}

function Removed({ seconds, label }) {
  return seconds >= 30 ? <span className="ts-removed"><span>−{duration(seconds)}</span> {label}</span> : null;
}

const removedOf = (stops, key) => stops.reduce((total, stop) => total + stop[key], 0);
const ORIGINS = { day_start: "début de journée", visit: "après une livraison" };

function TripRow({ voyage, number, currency, removedLabel }) {
  // Chargé PM saisi par son seul « Quitté à » : sa route vers la meunerie est comptée dans son chargement.
  const included = voyage.loads.filter((stop) => stop.included);
  return <tbody className="ts-trip" id={`voyage-${number}`}>
    <tr>
      <th scope="row" data-label="Date"><b>{shortDay(voyage.date)}</b><small>{truckLabel(voyage)}</small></th>
      <td data-label="Meunerie"><b>{voyage.loads.map((stop) => stop.site).join(" + ") || "—"}</b><small>{ORIGINS[voyage.origin] || ""}</small></td>
      <td className="ts-r" data-label="Route">{voyage.loads.length ? <>{about(voyage.estimatedLoads, voyage.toMill.seconds)}<small>{kmLabel(voyage.toMill.km, 1)}</small><Estimated on={voyage.estimatedLoads} /></> : "—"}<Removed seconds={removedOf(voyage.loads, "removedRoad")} label={removedLabel} /></td>
      <td className="ts-r" data-label="Chargement">{voyage.loads.length ? <>{about(voyage.estimatedLoads, voyage.loading.seconds)}<Estimated on={voyage.estimatedLoads} /></> : "—"}
        {included.length ? <small className="ts-included">{joined("route comprise", kmLabel(sumKnown(included.map((stop) => stop.km)), 1))}</small> : null}
        <Removed seconds={removedOf(voyage.loads, "removedSite")} label={removedLabel} /></td>
      <td data-label="Ferme livrée"><b>{voyage.pm ? "Chargé PM" : voyage.drops.map((stop) => stop.site).join(" + ") || "—"}</b>
        {voyage.pm ? <small>{pmDelivery(voyage.pm)}</small> : null}
        {voyage.orders.length ? <small className="ts-refs">
          {orderRefs(voyage.orders).map((reference, index) => <React.Fragment key={index}>{index ? ", " : ""}<span>{reference}</span></React.Fragment>)}</small> : null}
        <small><b>{voyage.pm ? voyage.loading.tons == null ? "" : `${tonsLabel(voyage.loading.tons)} chargées` : tonsLabel(voyage.tons)}</b></small></td>
      <td className="ts-r" data-label="Route">{voyage.drops.length ? <>{about(voyage.estimatedDrops, voyage.toClient.seconds)}<small>{kmLabel(voyage.toClient.km, 1)}</small><Estimated on={voyage.estimatedDrops} /></> : "—"}<Removed seconds={removedOf(voyage.drops, "removedRoad")} label={removedLabel} /></td>
      <td className="ts-r" data-label="Sur place">{voyage.drops.length ? <>{about(voyage.estimatedDrops, voyage.onsite)}<Estimated on={voyage.estimatedDrops} /></> : "—"}<Removed seconds={removedOf(voyage.drops, "removedSite")} label={removedLabel} /></td>
      <td className="ts-r" data-label="Heures">{hoursLabel(voyage.hours)}</td>
      <td className="ts-r ts-m" data-label="Montant">{formatBillingMoney(voyage.amount, currency)}
        {voyage.extras.map((extra, index) => <small key={index}>dont {extra.label} {formatBillingMoney(extra.amount, currency)}</small>)}</td>
    </tr>
    <tr className="ts-bar-row" aria-hidden="true"><td colSpan={9}><div className="ts-minibar">
      {voyage.segments.map((segment, index) => <span key={index} className={`is-${segment.kind}`} style={{ flexGrow: segment.seconds }} />)}
    </div></td></tr>
  </tbody>;
}

const LEGEND = [["road", "Route"], ["loading", "Chargement"], ["delivery", "Livraison sur place"]];

function Deliveries({ model, voyages, first, last, page, tabs, removedLabels }) {
  const { counts, currency, preloads } = model;
  const total = (pick) => sumKnown(model.voyages.map(pick));
  const estimatedLoads = model.voyages.some((voyage) => voyage.estimatedLoads);
  const estimatedDrops = model.voyages.some((voyage) => voyage.estimatedDrops);
  const garage = garageWords(model.time.route, "le ");
  const tons = counts.tons == null ? null : tonsLabel(counts.tons).replace(" t", "");
  return <section className="ts-sheet ts-detail" id={first ? "ts-livraisons" : undefined} aria-label={first ? "02 Vos livraisons" : "02 Vos livraisons, suite"}>
    <Tabs tabs={tabs} active="02" />
    <div className="ts-inner">
      <SectionHead number="02" title={first ? "Vos livraisons" : "Vos livraisons (suite)"} note="Une ligne par voyage, du chargement en meunerie jusqu’à la ferme" />
      {first ? <p className="ts-intro">Chaque voyage additionne la route jusqu’à la meunerie, le chargement, la route jusqu’à votre producteur et le temps passé sur place.{garage ? ` La route comprend aussi ${garage}.` : ""}</p> : null}
      <table className="ts-trips min-w-0">
        <colgroup><col className="ts-c-voyage" /><col className="ts-c-mill" /><col className="ts-c-time" /><col className="ts-c-time" /><col className="ts-c-client" />
          <col className="ts-c-time" /><col className="ts-c-onsite" /><col className="ts-c-hours" /><col className="ts-c-money" /></colgroup>
        <thead>
          <tr className="ts-grp"><th aria-hidden="true" /><th colSpan={3}>À la meunerie</th><th colSpan={3}>Chez votre producteur</th><th colSpan={2} aria-hidden="true" /></tr>
          <tr><th scope="col">Date</th><th scope="col">Meunerie</th><th scope="col" className="ts-r">Route</th><th scope="col" className="ts-r">Charg.</th>
            <th scope="col">Ferme livrée</th><th scope="col" className="ts-r">Route</th><th scope="col" className="ts-r">Sur place</th>
            <th scope="col" className="ts-r">Heures</th><th scope="col" className="ts-r">Montant</th></tr>
        </thead>
        {voyages.map((voyage) => <TripRow key={voyage.key} voyage={voyage} number={model.voyages.indexOf(voyage) + 1} currency={currency}
          removedLabel={removedLabels.get(voyage.key) || "retiré"} />)}
        {last && preloads.count ? <tbody className="ts-preloads"><tr>
          <th scope="row" colSpan={7}>Préchargements<small>{plural(preloads.count, "préchargement")} sur la période</small></th>
          <td className="ts-r" data-label="Heures">{hoursLabel(preloads.hours)}</td>
          <td className="ts-r" data-label="Montant">{formatBillingMoney(preloads.amount, currency)}</td>
        </tr></tbody> : null}
        {last ? <tfoot><tr>
          <th scope="row" data-label="Total">{preloads.count ? "Total" : plural(model.voyages.length, "voyage")}</th><td className="ts-empty" />
          <td className="ts-r" data-label="Route">{about(estimatedLoads, total((voyage) => voyage.toMill.seconds))}</td>
          <td className="ts-r" data-label="Chargement">{about(estimatedLoads, sumKnown([total((voyage) => voyage.loading.seconds), preloads.count ? preloads.hours == null ? null : preloads.hours * 3600 : 0]))}</td>
          <td data-label="Fermes livrées">{joined(plural(counts.farms, "ferme"), plural(counts.orders, "commande"))}</td>
          <td className="ts-r" data-label="Route">{about(estimatedDrops, total((voyage) => voyage.toClient.seconds))}</td>
          <td className="ts-r" data-label="Sur place">{about(estimatedDrops, total((voyage) => voyage.onsite))}</td>
          <td className="ts-r" data-label="Heures">{hoursLabel(model.hours)}</td>
          <td className="ts-r" data-label="Montant">{formatBillingMoney(total((voyage) => voyage.amount) + preloads.amount, currency)}</td>
        </tr></tfoot> : null}
      </table>
      {last ? <>
        <div className="ts-legend">{LEGEND.map(([kind, label]) => <span key={kind}><i className={`is-${kind}`} />{label}</span>)}</div>
        {estimatedLoads || estimatedDrops ? <p className="ts-estimated-note">≈ Temps estimé : l’heure d’arrivée à cet arrêt n’a pas été relevée. La durée de l’arrêt, les heures et le montant sont exacts.</p> : null}
        {model.others.length ? <table className="ts-others min-w-0"><caption>Autres frais, avant surtaxe et taxes</caption><tbody>
          {model.others.map((other, index) => <tr key={index}><td>{other.label}</td><td className="ts-r">{formatBillingMoney(other.amount, currency)}</td></tr>)}
        </tbody></table> : null}
      </> : null}
    </div>
    {last && model.voyages.length <= TRIPS_FIRST_PAGE && tons ? <Band photo="ferme-banniere.jpg" note={capital(periodText(model.periodStart, model.periodEnd))}
      title={<>{tons} tonnes<br />livrées chez {counts.farms > 1 ? `${counts.farms} de vos producteurs` : "l’un de vos producteurs"}.</>} /> : null}
    <Folio page={page} />
  </section>;
}

function ProofTable({ proof }) {
  return <table className="ts-cmp min-w-0">
    <thead><tr><th scope="col">Lieu</th><th scope="col">Arrivée Geotab</th>
      <th scope="col">Départ Geotab</th><th scope="col">Distance du site</th></tr></thead>
    <tbody>{proof.rows.map((row, index) => <tr key={row.key}>
      <th scope="row">{row.kind === "start" ? <>{row.label}{row.masked ? <> <span className="ts-prev">autre client</span></> : null}</>
        : <><b>{index}. {row.label}</b> · {row.kind === "loading" ? "chargement" : "livraison"}</>}</th>
      <td className="is-gps" data-label="Arrivée Geotab">{minuteClock(row.gpsArrival)}</td>
      <td className="is-gps" data-label="Départ Geotab">{minuteClock(row.gpsDeparture)}</td>
      <td data-label="Distance du site">{row.distance === null || row.distance === undefined ? "" : distanceLabel(row.distance)}</td>
    </tr>)}</tbody>
  </table>;
}

function Journey({ voyage, proof }) {
  const zone = voyage.timezone;
  const start = proof?.rows[0];
  const dayStart = start ? !start.masked && start.label === "Début de journée" : voyage.origin === "day_start";
  const label = proof?.removedLabel || "retiré";
  return <div className="ts-journey">
    <div className="ts-node is-start"><div className="ts-dot"><Icon name="start" /></div>
      <b>{start?.label || (dayStart ? "Début de journée" : "Livraison précédente")}</b>{!proof ? <span className="ts-num">{clock(voyage.departure, zone)}</span> : null}
      {(start ? start.masked : !dayStart) ? <small>autre client</small> : null}</div>
    {[...voyage.loads, ...voyage.drops].map((stop, index) => {
      const drop = stop.kind === "delivery" || stop.kind === "dompeur";
      return <React.Fragment key={index}>
        <div className="ts-leg"><span>{stop.included ? "route comprise dans le chargement" : <b className="ts-num">{about(stop.estimated, stop.travel)}</b>}{stop.km != null ? ` · ${kmLabel(stop.km, 1)}` : ""}</span><Removed seconds={stop.removedRoad} label={label} /></div>
        <div className="ts-node"><div className="ts-dot"><Icon name={drop ? "farm" : "mill"} /></div>
          <b>{proof ? `${index + 1}. ${drop ? "Livraison" : "Chargement"}` : stop.site}</b>{!proof ? <span className="ts-num">{[clock(stop.arrival, zone), clock(stop.departure, zone)].filter(Boolean).join(" → ")}</span> : null}
          <small>{drop ? joined("sur place", about(stop.estimated, stop.onsite), stop.estimated ? "estimé" : "", `${stop.orders.length} cmd`) : joined("chargement", about(stop.estimated, stop.onsite), stop.estimated ? "estimé" : "", tonsLabel(stop.tons))}</small></div>
      </React.Fragment>;
    })}
  </div>;
}

function VoyageProof({ voyage, number, proof, currency }) {
  return <article className="ts-tour" id={`geotab-${number}`} aria-label={`Données Geotab · ${longDay(voyage.date)} · ${truckLabel(voyage)}`}>
    <header className="ts-voy-h">
      <b>{longDay(voyage.date)}</b><span>{truckLabel(voyage)}</span>{voyage.pm ? <span>Chargé PM · {pmDelivery(voyage.pm)}</span> : null}<span className="ts-sp" />
      <span className="ts-amt ts-num">{formatBillingMoney(voyage.amount, currency)}</span>
    </header>
    <Journey voyage={voyage} proof={proof} />
    {proof ? <ProofTable proof={proof} /> : <p className="ts-no-gps">Les données Geotab de ce voyage ne sont pas disponibles.</p>}
  </article>;
}

function Proofs({ model, voyages, first, last, page, tabs, proofs }) {
  return <section className="ts-sheet ts-annex" id={first ? "ts-geotab" : undefined} aria-label={first ? "03 Données Geotab" : "03 Données Geotab, suite"}>
    <Tabs tabs={tabs} active="03" />
    <div className="ts-inner">
      <div className="ts-geotab-head">
        <SectionHead number="03" title={first ? "Données Geotab" : "Données Geotab (suite)"} note="Informations de nos camionneurs recueillies avec Geotab" />
        <img src={GEOTAB_LOGO} alt="Geotab" />
      </div>
      {voyages.map((voyage) => <VoyageProof key={voyage.key} voyage={voyage} number={model.voyages.indexOf(voyage) + 1}
        proof={proofs.get(voyage.key) || null} currency={model.currency} />)}
      {last ? <p className="ts-gps-note">Données recueillies avec Geotab, utilisé dans chacun de nos camions. La distance du site est mesurée entre l’arrêt du camion et l’adresse du site. Les arrêts en route ne sont pas détaillés.</p> : null}
    </div>
    {last && voyages.length === 1 ? <Band photo="route.jpg" title="Le suivi de vos transports, du premier au dernier arrêt." note="Geotab utilisé dans toute la flotte" /> : null}
    <Folio page={page} />
  </section>;
}

function Back({ model }) {
  const { issuer } = model;
  return <section className="ts-sheet ts-back-page" id="ts-nous-joindre" aria-label="Dos du relevé">
    <Photo name="campagne.jpg" /><div className="ts-shade" />
    <div className="ts-back-body">
      <div className="ts-label">Merci</div>
      <h2>Merci de votre confiance.</h2>
      <div className="ts-rule" />
      <div className="ts-contacts">
        <div><span>Facturation</span><b>Questions sur ce relevé</b><small>{issuer.email}{issuer.email && issuer.phone ? <br /> : null}{issuer.phone}</small></div>
        {issuer.address ? <div><span>Nous joindre</span><b>{issuer.name || "Transporteur"}</b><small>{issuer.address}</small></div> : null}
      </div>
      <p className="ts-legal">Ce relevé présente les services de transport effectués pour vous pendant la période indiquée. Il ne constitue pas une facture : la facture officielle, {model.amounts.taxes ? "du même montant" : "avec les taxes applicables"}, vous sera transmise séparément.</p>
      <Brand model={model} light />
    </div>
  </section>;
}

/** Version en ligne seulement : chaque commande livrée, avec un lien vers la ligne de son voyage. */
function Orders({ model, tabs }) {
  const rows = model.voyages.flatMap((voyage, index) => voyage.deliveries.map((delivery) => ({ ...delivery, voyage, number: index + 1 })));
  return <section className="ts-sheet ts-detail" id="ts-commandes" aria-label="Vos commandes">
    <Tabs tabs={tabs} active="02" />
    <div className="ts-inner">
      <SectionHead number="02" title="Vos commandes" note="Chaque commande livrée pendant la période, avec son voyage" />
      <table className="ts-trips ts-orders min-w-0">
        <thead><tr><th scope="col">Commande</th><th scope="col">Ferme livrée</th><th scope="col">Livrée le</th><th scope="col" className="ts-r">Tonnes</th><th scope="col">Voyage</th></tr></thead>
        <tbody>{rows.map((row) => <tr key={`${row.number}-${row.reference}`}>
          <th scope="row" data-label="Commande"><b>{row.reference}</b></th><td data-label="Ferme livrée">{row.farm}</td>
          <td data-label="Livrée le">{shortDay(row.voyage.date)}</td><td className="ts-r" data-label="Tonnes">{tonsLabel(row.tons) || "—"}</td>
          <td data-label="Voyage"><a href={`#voyage-${row.number}`}>{truckLabel(row.voyage)}</a></td>
        </tr>)}</tbody>
        <tfoot><tr><th scope="row">{plural(rows.length, "commande")}</th><td /><td /><td className="ts-r">{tonsLabel(sumKnown(rows.map((row) => row.tons))) || "—"}</td><td /></tr></tfoot>
      </table>
    </div>
    <Folio page="En ligne" />
  </section>;
}

/** Ce que le courriel et la version en ligne reprennent du relevé, formaté comme sur le PDF. Les nombres bruts servent au fichier Excel. */
export function statementDigest(model) {
  const { amounts, counts, currency, preloads } = model;
  const money = (value) => formatBillingMoney(value, currency);
  const decimal = new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 2, useGrouping: false });
  const raw = (value) => value == null ? "" : decimal.format(value);
  const rate = model.rate ? billingRateLabel(model.rate.rate, "heure", currency) : "";
  return {
    number: model.number, client: model.clientName, period: periodText(model.periodStart, model.periodEnd),
    amount: money(billedAmount(amounts)), taxes: amounts.taxes,
    // Relevé taxé : le total avant taxes, puis la TPS et la TVQ ; « amount » est alors taxes comprises.
    before_taxes: money(amounts.beforeTaxes), tax_lines: taxLines(amounts).map(([label, value]) => [label, money(value)]),
    breakdown: [
      [joined("Transport", rate ? `${hoursLabel(model.hours)} × ${rate}` : hoursLabel(model.hours)), money(amounts.transport)],
      amounts.extras ? ["Autres frais", money(amounts.extras)] : null,
      amounts.surchargePercent ? [`Surtaxe carburant · ${decimal.format(amounts.surchargePercent)} %`, money(amounts.surcharge)] : null,
    ].filter(Boolean),
    figures: [
      [String(counts.voyages), counts.voyages > 1 ? "Voyages" : "Voyage"],
      counts.tons == null ? null : [tonsLabel(counts.tons), "Tonnes livrées"],
      [hoursLabel(model.hours), "Heures"],
      counts.km == null ? null : [kmLabel(counts.km), "Parcourus"],
      preloads.count ? [String(preloads.count), preloads.count > 1 ? "Préchargements" : "Préchargement"] : null,
    ].filter(Boolean),
    voyages: model.voyages.map((voyage, index) => ({
      number: index + 1, date: voyage.date, day: shortDay(voyage.date), truck: truckLabel(voyage),
      mill: voyage.loads.map((stop) => stop.site).join(" + "),
      farm: voyage.pm ? `Chargé PM · ${pmDelivery(voyage.pm)}` : voyage.drops.map((stop) => stop.site).join(" + "),
      orders: voyage.orders, tons: raw(voyage.tons), hours: raw(voyage.hours), amount: raw(voyage.amount), amountLabel: money(voyage.amount),
    })),
    orders: model.voyages.flatMap((voyage, index) => voyage.deliveries.map((delivery) => ({ ...delivery, tons: raw(delivery.tons), date: voyage.date, voyage: index + 1 }))),
  };
}

/** Le relevé remis à la Coop, comme un cartable : une feuille par page, numérotées pour le sommaire. « online » ajoute les commandes. */
export default function TransportStatementView({ model, proofs = new Map(), online = false }) {
  const withProofs = model.voyages.some((voyage) => proofs.get(voyage.key));
  const tripPages = chunk(model.voyages, TRIPS_FIRST_PAGE, TRIPS_PER_PAGE, tripWeight);
  const proofPages = withProofs ? chunk(model.voyages, PROOFS_PER_PAGE, PROOFS_PER_PAGE) : [];
  const removedLabels = new Map(model.voyages.map((voyage) => [voyage.key, proofs.get(voyage.key)?.removedLabel]));
  const tabs = withProofs ? ["01", "02", "03"] : ["01", "02"];
  const firstProofPage = 4 + tripPages.length;
  const sections = [
    { number: "01", title: "L’essentiel", note: "Montant, calcul des heures, chiffres clés", page: 3 },
    { number: "02", title: "Vos livraisons", note: "Une ligne par voyage, du chargement à la ferme", page: 4 },
    ...(withProofs ? [{ number: "03", title: "Données Geotab", note: "Informations recueillies dans nos camions", page: firstProofPage }] : []),
  ];
  sections.push({ number: String(sections.length + 1).padStart(2, "0"), title: "Nous joindre", note: "Vos interlocuteurs", page: firstProofPage + proofPages.length });
  return <div className="ts-document">
    <Icons />
    <Cover model={model} />
    <Letter model={model} sections={sections} />
    <Essentials model={model} page={3} tabs={tabs} />
    {tripPages.map((voyages, index) => <Deliveries key={`trips-${index}`} model={model} voyages={voyages} first={index === 0} last={index === tripPages.length - 1}
      page={4 + index} tabs={tabs} removedLabels={removedLabels} />)}
    {online ? <Orders model={model} tabs={tabs} /> : null}
    {proofPages.map((voyages, index) => <Proofs key={`proofs-${index}`} model={model} voyages={voyages} first={index === 0} last={index === proofPages.length - 1}
      page={firstProofPage + index} tabs={tabs} proofs={proofs} />)}
    <Back model={model} />
  </div>;
}
